import React, { useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import FormItem from "@components/FormItem/FormItem";
import type { Option } from "@components/FormItem/FormItemFieldRenderer";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import type { SequenceRequest, SequenceView } from "@services/drybulkCargoOperationsApi";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../../constants";
import { useCachedCargoOperationDetail } from "../../hooks/useCargoOperationDetail";
import { useErrorToast } from "../../hooks/useErrorToast";
import { useSequenceMutations } from "../../hooks/useSequenceMutations";
import { useSequenceOptions } from "../../hooks/useSequenceOptions";
import { optionLabel } from "../../utils/optionLabel";
import { problemMessage, problemStatus } from "../../utils/problemDetail";
import {
  SEQUENCE_PANEL_FIELDS as FIELDS,
  HOLD_OPTIONS,
  MAX_ACTUAL_SIZE_LENGTH,
} from "./SequencePanel.constants";
import {
  sequencePanelDefaultValues,
  sequencePanelSchema,
  type SequencePanelFormData,
} from "./SequencePanel.schema";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;

interface SequencePanelProps {
  operationId: string;
  sequence?: SequenceView | null;
  onClose: () => void;
}
const withSavedValue = (options: Option[], saved: string | null | undefined): Option[] =>
  saved && !options.some((option) => option.value === saved)
    ? [...options, { label: saved, value: saved }]
    : options;
const sortOptions = (options: Option[]): Option[] =>
  [...options].sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );

const SORTED_HOLD_OPTIONS = sortOptions(HOLD_OPTIONS);

const toDefaultValues = (sequence?: SequenceView | null): SequencePanelFormData =>
  sequence
    ? {
        plannedQuantity: sequence.plannedQuantity === null ? "" : String(sequence.plannedQuantity),
        hold: String(sequence.hold),
        shipperCompanyCode: sequence.shipperCompanyCode ?? "",
        stockPileCode: sequence.stockPileCode ?? "",
        cargoCode: sequence.cargoCode ?? "",
        gradeCode: sequence.gradeCode ?? "",
        gradeSizeCode: sequence.gradeSizeCode ?? "",
        actualSize: sequence.actualSize ?? "",
      }
    : sequencePanelDefaultValues;
const SequencePanel: React.FC<SequencePanelProps> = ({ operationId, sequence = null, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const options = useSequenceOptions(true);
  const { add, update } = useSequenceMutations(operationId);
  const isUpdate = sequence !== null;
  const remained = useCachedCargoOperationDetail(operationId).data?.totalQuantityRemained ?? null;
  const remainedText = remained === null ? null : String(remained);

  const schema = useMemo(() => sequencePanelSchema(t), [t]);
  const form = useForm<SequencePanelFormData>({
    resolver: zodResolver(schema),
    defaultValues: toDefaultValues(sequence),
  });
  useErrorToast(options.isError, t(`${L}.panel.options-error`));
  const { handleSubmit, setValue, watch } = form;
  const cargoCode = watch(FIELDS.CARGO_CODE);

  const cargoOptions = useMemo<Option[]>(
    () =>
      sortOptions(
        withSavedValue(
          (options.cargoCodes ?? []).map((cargo) => ({
            label: optionLabel(cargo.cargoCode, cargo.description),
            value: cargo.cargoCode,
          })),
          sequence?.cargoCode,
        ),
      ),
    [options.cargoCodes, sequence?.cargoCode],
  );
  const gradeOptions = useMemo<Option[]>(
    () =>
      sortOptions(
        withSavedValue(
          (options.grades ?? []).map((grade) => ({
            label: optionLabel(grade.gradeCode, grade.description),
            value: grade.gradeCode,
          })),
          sequence?.gradeSizeCode,
        ),
      ),
    [options.grades, sequence?.gradeSizeCode],
  );
  const shipperOptions = useMemo<Option[]>(
    () =>
      sortOptions(
        withSavedValue(
          (options.shippers ?? []).map((shipper) => ({
            label: optionLabel(shipper.companyCode, shipper.name),
            value: shipper.companyCode,
          })),
          sequence?.shipperCompanyCode,
        ),
      ),
    [options.shippers, sequence?.shipperCompanyCode],
  );
  const stockPileOptions = useMemo<Option[]>(
    () =>
      sortOptions(
        withSavedValue(
          (options.stockPiles ?? []).map((pile) => ({
            label: optionLabel(pile.stockPileCode, pile.name),
            value: pile.stockPileCode,
          })),
          sequence?.stockPileCode,
        ),
      ),
    [options.stockPiles, sequence?.stockPileCode],
  );

  useEffect(() => {
    const selected = options.cargoCodes?.find((cargo) => cargo.cargoCode === cargoCode);
    const saved = sequence && sequence.cargoCode === cargoCode ? sequence.gradeCode : null;
    setValue(FIELDS.GRADE_CODE, selected?.gradeCode ?? saved ?? "");
  }, [cargoCode, options.cargoCodes, sequence, setValue]);

  const onValid = async (values: SequencePanelFormData) => {
    const request: SequenceRequest = {
      plannedQuantity: Number(values.plannedQuantity),
      hold: Number(values.hold),
      shipperCompanyCode: values.shipperCompanyCode,
      stockPileCode: values.stockPileCode,
      cargoCode: values.cargoCode,
      gradeSizeCode: values.gradeSizeCode,
      actualSize: values.actualSize?.trim() ? values.actualSize.trim() : null,
    };
    try {
      if (sequence) {
        await update.mutateAsync({ sequenceId: sequence.id, request });
      } else {
        await add.mutateAsync(request);
      }
      onClose();
    } catch (error) {
      const message = problemMessage(error);
      if (problemStatus(error) === 409) {
        notify({
          type: "error",
          title: t(`${L}.notifications.change-rejected-title`),
          description: message ?? t(`${L}.notifications.change-failed`),
        });
        onClose();
      } else {
        notify({
          type: "error",
          title: t("common.error"),
          description: message ?? t(`${L}.panel.save-failed`),
        });
      }
    }
  };

  const onInvalid = () => {
    notify({
      type: "error",
      title: t(`${L}.notifications.request-submitted-error-title`),
      description: t(`${L}.notifications.request-submitted-error-description`),
    });
  };

  const isSubmitting = add.isPending || update.isPending;

  return (
    <SideDrawer
      isOpen
      title={t(`${L}.panel.${isUpdate ? "title-update" : "title-add"}`)}
      onClose={onClose}
      onSubmit={handleSubmit(onValid, onInvalid)}
      isSubmitting={isSubmitting}
      isSubmitDisabled={options.isLoading || options.isError}
      submitLabel={t(`${L}.panel.${isUpdate ? "submit-update" : "submit-add"}`)}
      width={720}
      cancelLabel={t(`${L}.panel.cancel`)}
    >
      <div className="w-full flex flex-col gap-2 px-6 py-2">
        {options.isLoading && <CircularProgress aria-label={t(`${L}.panel.options-loading`)} />}
        {!isUpdate && remained !== null && remained <= 0 && (
          <div role="status" aria-live="polite" data-testid="sequence-panel-remained-warning">
            <Alert type="warning">
              {remained === 0
                ? t(`${L}.panel.remained-zero`)
                : t(`${L}.panel.remained-over`, { quantity: remainedText?.replace(/^-/, "") })}
            </Alert>
          </div>
        )}
        <FormProvider {...form}>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <FormItem<SequencePanelFormData, "plannedQuantity">
              required
              name={FIELDS.PLANNED_QUANTITY}
              type="text"
              label={t(`${L}.fields.quantity`)}
              inputLabel={t(`${L}.fields.quantity-placeholder`)}
              testId="sequence-panel-quantity"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
            <FormItem<SequencePanelFormData, "hold">
              required
              name={FIELDS.HOLD}
              type="autocomplete"
              label={t(`${L}.fields.hold`)}
              testId="sequence-panel-hold"
              options={SORTED_HOLD_OPTIONS}
            />
            <FormItem<SequencePanelFormData, "shipperCompanyCode">
              required
              name={FIELDS.SHIPPER_COMPANY_CODE}
              type="autocomplete"
              label={t(`${L}.fields.shipper`)}
              testId="sequence-panel-shipper"
              options={shipperOptions}
            />
            <FormItem<SequencePanelFormData, "stockPileCode">
              required
              name={FIELDS.STOCK_PILE_CODE}
              type="autocomplete"
              label={t(`${L}.fields.stock-pile`)}
              testId="sequence-panel-stock-pile"
              options={stockPileOptions}
            />
            <FormItem<SequencePanelFormData, "cargoCode">
              required
              name={FIELDS.CARGO_CODE}
              type="autocomplete"
              label={t(`${L}.fields.cargo-code`)}
              testId="sequence-panel-cargo-code"
              options={cargoOptions}
            />
            <FormItem<SequencePanelFormData, "gradeCode">
              disabled
              name={FIELDS.GRADE_CODE}
              type="text"
              label={t(`${L}.fields.cargo-type-code`)}
              testId="sequence-panel-grade-code"
            />
            <FormItem<SequencePanelFormData, "gradeSizeCode">
              required
              name={FIELDS.GRADE_SIZE_CODE}
              type="autocomplete"
              label={t(`${L}.fields.grade-size`)}
              testId="sequence-panel-grade"
              options={gradeOptions}
            />
            <FormItem<SequencePanelFormData, "actualSize">
              name={FIELDS.ACTUAL_SIZE}
              type="text"
              label={t(`${L}.fields.actual-size`)}
              testId="sequence-panel-actual-size"
              maxChars={MAX_ACTUAL_SIZE_LENGTH}
            />
          </div>
        </FormProvider>
      </div>
    </SideDrawer>
  );
};

export default SequencePanel;
